import type { IntlShape } from 'react-intl'
import { formatArrowSeparator } from 'LuxKit/intl/separators'
import { getEsimUpsellTicketMetadata } from 'loyalty/lib/esims/esimUpsellTicketUtils'

export function formatEsimInternationalDataRowHeading(
  intl: IntlShape,
  ticketId: App.LoyaltyEsimUpsellTicketId,
) {
  const metadata = getEsimUpsellTicketMetadata(ticketId)

  if (metadata.kind === 'gb_pack') {
    return intl.formatMessage(
      {
        id: '7mK-OaPGeHWvL6w2E7q-',
        defaultMessage: '{gb} GB International Data',
        description: 'eSIM data benefit label on the upcoming escapes card',
      },
      { gb: metadata.gb },
    )
  }

  return intl.formatMessage(
    {
      id: 'jR0YZ3M1XU29LWlgHD7j',
      defaultMessage: '{days, plural, one {# Day Unlimited International Data} other {# Days Unlimited International Data}}',
      description: 'eSIM unlimited data benefit label on the upcoming escapes card showing the duration allowance',
    },
    { days: metadata.days },
  )
}

export function formatEsimPackageLabel(
  intl: IntlShape,
  ticketId: App.LoyaltyEsimUpsellTicketId,
) {
  const metadata = getEsimUpsellTicketMetadata(ticketId)

  if (metadata.kind === 'gb_pack') {
    return intl.formatMessage(
      {
        id: 'QIgvRmD6-n_Dzxl_6rsV',
        defaultMessage: '{gb}GB',
        description: 'Data amount label for a fixed-data eSIM upgrade package selected by the member',
      },
      { gb: metadata.gb },
    )
  }

  return intl.formatMessage(
    {
      id: 'IXkouH9Js4uYpngeWDkK',
      defaultMessage: '{days, plural, one {# Day Unlimited} other {# Days Unlimited}}',
      description: 'Duration label for an unlimited-days eSIM upgrade package selected by the member',
    },
    { days: metadata.days },
  )
}

export function formatEsimPackageTransitionLabel(
  intl: IntlShape,
  fromTicketId: App.LoyaltyEsimUpsellTicketId,
  toTicketId: App.LoyaltyEsimUpsellTicketId,
) {
  return intl.formatMessage(
    {
      id: 'KoedLGi1DQRATeuTCqVz',
      defaultMessage: '{fromPackage} {separator} {toPackage}',
      description: 'Checkout summary label for an eSIM package upgrade showing the current package, a right-arrow separator (→), and the newly selected package',
    },
    {
      fromPackage: formatEsimPackageLabel(intl, fromTicketId),
      toPackage: formatEsimPackageLabel(intl, toTicketId),
      separator: formatArrowSeparator(intl),
    },
  )
}

export function formatEsimDataRoamingHeading(
  intl: IntlShape,
  ticketId: App.LoyaltyEsimUpsellTicketId,
  currentGb?: number,
) {
  const metadata = getEsimUpsellTicketMetadata(ticketId)

  if (metadata.kind === 'gb_pack') {
    if (currentGb && metadata.gb > currentGb) {
      return intl.formatMessage(
        {
          id: 'DSJMmEQ1yFYVTNEZ5rc8',
          defaultMessage: '{gb}GB Data Roaming ({current}GB complimentary + {added} GB added)',
          description: 'Heading for eSIM tile after selecting a fixed-data upsell larger than the member’s complimentary allowance',
        },
        {
          gb: metadata.gb,
          current: currentGb,
          added: metadata.gb - currentGb,
        },
      )
    }

    return intl.formatMessage(
      {
        id: 'ZKIyaL4bNZ5r7rOJViD_',
        defaultMessage: '{gb}GB Data Roaming',
        description: 'Heading for eSIM tile after selecting a fixed-data upsell package',
      },
      { gb: metadata.gb },
    )
  }

  return intl.formatMessage(
    {
      id: 'HEQcnO1QnFO3ffShJgdZ',
      defaultMessage: '{days, plural, one {# Day Unlimited Data Roaming} other {# Days Unlimited Data Roaming}}',
      description: 'Heading for eSIM tile after selecting an unlimited-days upsell package',
    },
    { days: metadata.days },
  )
}

export function formatEsimDataRoamingDescription(
  intl: IntlShape,
  ticketId: App.LoyaltyEsimUpsellTicketId,
) {
  const metadata = getEsimUpsellTicketMetadata(ticketId)

  if (metadata.kind === 'gb_pack') {
    return intl.formatMessage(
      {
        id: 'quTSRSQFIxfBeFhwYSDh',
        defaultMessage: 'Stay connected on this international trip with {gb}GB of mobile data.',
        description: 'Description for eSIM tile after selecting a fixed-data upsell package for the current booking',
      },
      { gb: metadata.gb },
    )
  }

  return intl.formatMessage(
    {
      id: '1vQroF-qY1E9BQmy_7bA',
      defaultMessage: 'Stay connected on this international trip with {days, plural, one {# day} other {# days}} of unlimited mobile data.',
      description: 'Description for eSIM tile after selecting an unlimited-days upsell package for the current booking',
    },
    { days: metadata.days },
  )
}

export function formatEsimUpgradeActiveLine(
  intl: IntlShape,
  ticketId: App.LoyaltyEsimUpsellTicketId,
) {
  const metadata = getEsimUpsellTicketMetadata(ticketId)

  if (metadata.kind === 'gb_pack') {
    return intl.formatMessage(
      {
        id: 'q0CnMSt0SAVAAmlMjDKe',
        defaultMessage: 'Your {gb}GB upgrade is active for this booking.',
        description: 'Second-line note in the eSIM details modal for an active fixed-data upgrade package',
      },
      { gb: metadata.gb },
    )
  }

  return intl.formatMessage(
    {
      id: 'lTr-9tjaNXvJR8ZbUfys',
      defaultMessage: 'Your {days, plural, one {# Day Unlimited} other {# Days Unlimited}} upgrade is active for this booking.',
      description: 'Second-line note in the eSIM details modal for an active unlimited-days upgrade package',
    },
    { days: metadata.days },
  )
}

export function formatEsimPromoCodeDataBadgeLabel(
  intl: IntlShape,
  options: {
    dataAllowanceGb?: number
    unlimitedDays?: number
  },
): string | undefined {
  if (options.unlimitedDays) {
    return intl.formatMessage(
      {
        id: '8IbWEVyeZSGs6UzK0xdF',
        defaultMessage: '{days, plural, one {# Day Unlimited} other {# Days Unlimited}}',
        description: 'Data allowance badge on the eSIM promo code card for an unlimited-days package',
      },
      { days: options.unlimitedDays },
    )
  }

  if (options.dataAllowanceGb) {
    return intl.formatMessage(
      {
        id: 'kbOjGIMq5fpuQI9S1nHJ',
        defaultMessage: '{amount} GB',
        description: 'eSIM data allowance per tier in benefits table (e.g. 1 GB, 3 GB)',
      },
      { amount: options.dataAllowanceGb },
    )
  }

  return undefined
}

export function getEsimBenefitTileMessages(
  intl: IntlShape,
  options: {
    includedGb: number
    selectedPackageTicketId?: App.LoyaltyEsimUpsellTicketId
  },
) {
  const complimentaryHeading = intl.formatMessage(
    {
      id: 'Twf9q4A3YldgMqUZWnff',
      defaultMessage: 'Free {gbValue}GB travel eSIM',
      description: 'Heading text for the eSIM benefit tile on the order view page',
    },
    { gbValue: options.includedGb },
  )
  const complimentaryDescription = intl.formatMessage(
    {
      id: 'Tebh5x6k26R0Tdn5itpn',
      defaultMessage: 'Stay connected on this international trip with {gbValue}GB of complimentary mobile data.',
      description: 'Description text for the eSIM benefit tile on the order view page',
    },
    { gbValue: options.includedGb },
  )

  return {
    heading: options.selectedPackageTicketId
      ? formatEsimDataRoamingHeading(intl, options.selectedPackageTicketId, options.includedGb)
      : complimentaryHeading,
    description: options.selectedPackageTicketId
      ? formatEsimDataRoamingDescription(intl, options.selectedPackageTicketId)
      : complimentaryDescription,
    addedLabel: intl.formatMessage({
      id: 'RioreOBROGNRKdkgS55A',
      defaultMessage: 'Added',
      description: 'Status label indicating the eSIM benefit is included in the booking',
    }),
    detailsLabel: intl.formatMessage({
      id: 'IuiqtRmg4WQOcjfjB7cn',
      defaultMessage: 'Details',
      description: 'Button label to open the eSIM benefit details modal from tile',
    }),
  }
}
